import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ProfileFacetsService } from '@app/profile/services/profile-facets.service';
import { NormalizedRecommendation } from '@app/recommendations/recommendations.api';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import {
  DfIconArticle16,
  DfIconAssessment16,
  DfIconEpisode16,
  DfIconBook16,
  DfIconCourse16,
  DfIconEvent16,
  DfIconPlay16,
  DfIconRegistry,
  DfIconVideo16,
} from '@lib/fresco';
import { TranslateService } from '@ngx-translate/core';
import { ProfileFilter } from '../profile-filter-bar/profile-filter-bar.component';
import { ProfileSharedService } from './profile-shared.service';
import { TrackerService } from '@app/shared/services/tracker.service';

export interface UserProfileShared {
  incomplete: NormalizedRecommendation[];
  completed: NormalizedRecommendation[];
}

@Component({
  selector: 'dgx-profile-shared',
  templateUrl: './profile-shared.component.html',
  styleUrls: ['./profile-shared.component.scss'],
})
export class ProfileSharedComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public readonly CLASS_NAME = 'ProfileSharedComponent';

  @Input() public rowLength: number;
  public isLoading: boolean = true;
  public sharedItems: UserProfileShared = {
    incomplete: [],
    completed: [],
  };
  public filteredSharedItems: NormalizedRecommendation[];
  public hasOrg: boolean;
  public showSearchResults: boolean = false;
  public activeFilter: ProfileFilter;

  public i18n = this.translate.instant([
    'dgRecommendationsOverview_About',
    'dgRecommendationsOverview_AboutConsumer',
    'dgRecommendationsOverview_Filter',
    'dgRecommendationsOverview_Incomplete',
    'Core_Completed',
    'UserController_NoResults',
    'Core_Empty_Image_Alt',
    'dgRecommendationsOverview_EmptyStateTitle',
    'dgRecommendationsOverview_EmptyStateDescription',
    'dgRecommendationsOverview_Description',
    'ProfileCtrl_NoExactMatches',
    'ProfileCtrl_NoExactMatchesHelp',
  ]);

  public sharedHeader = this.translate.instant('Core_Shared');

  constructor(
    private translate: TranslateService,
    private profileSharedService: ProfileSharedService,
    private authService: AuthService,
    private profileFacetsService: ProfileFacetsService,
    private iconRegistry: DfIconRegistry,
    private cdr: ChangeDetectorRef,
    private trackerService: TrackerService
  ) {
    super();
    this.iconRegistry.registerIcons([
      DfIconArticle16,
      DfIconAssessment16,
      DfIconEpisode16,
      DfIconBook16,
      DfIconEvent16,
      DfIconCourse16,
      DfIconPlay16,
      DfIconVideo16,
    ]);
  }

  public ngOnInit(): void {
    this.trackerService.setLocation('Profile Shared');

    this.hasOrg = !!this.authService.authUser?.orgInfo.length;
    this.profileSharedService.sharedItems$
      .pipe(this.takeUntilDestroyed())
      .subscribe(
        ({ data, filter }) => {
          this.sharedItems = { ...data };
          if (this.activeFilter?.isActive) {
            this.applyFilter(this.activeFilter);
          } else {
            this.activeFilter = filter;
          }
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        () => (this.isLoading = false)
      );
  }

  public applyFilter(updatedFilter: ProfileFilter): void {
    const allAssignments = [
      ...this.sharedItems.incomplete,
      ...this.sharedItems.completed,
    ];
    const { data, filter } = this.profileFacetsService.doRecommendationsSearch(
      allAssignments,
      updatedFilter
    );
    this.filteredSharedItems = data;
    this.activeFilter = filter; // use the filter returned from doSearch, it has the updated counts

    this.showSearchResults = updatedFilter.isActive;
  }

  public isEmpty(): boolean {
    return (
      !this.isLoading &&
      !this.sharedItems.incomplete.length &&
      !this.sharedItems.completed.length
    );
  }

  public trackRecommendationById(
    index,
    recommendation: NormalizedRecommendation
  ): number {
    return recommendation.recommendationId;
  }

  public handleDismissOpportunity($event: NormalizedRecommendation): void {
    this.profileSharedService.dismiss($event).subscribe();
  }
}
